/**
 * Auth-boundary helper — dispatches sr:auth:userChanged AND reloads atomically.
 *
 * CRITICAL INVARIANT: dispatching sr:auth:userChanged without a following
 * window.location.reload() causes user-scoped state (VesselContext.currentVesselId,
 * UniversalSyncBridge.syncQueue, justPulledCollections, subscriptions) to leak
 * across users when Layer D remount is disabled (FT-365 Option B).
 *
 * All account-boundary events MUST go through this helper. Direct
 * dispatchEvent(new CustomEvent('sr:auth:userChanged', ...)) is forbidden by
 * ESLint rule (see .eslintrc no-restricted-syntax).
 *
 * See:
 *  - FT-365 saga (project_ft365_saga.md)
 *  - project_ft365_v3_5_hang_diagnosis.md
 *  - feedback_auth_boundary_reload_invariant.md
 */

import { isOptionBAuthBoundaryEnabled } from '../config/featureFlags';
import { track1 } from '../utils/track1Telemetry';

const LAST_USER_ID_KEY = 'sr.auth.lastUserId';

// FT-365 Option B HARDENING (2026-07-15): reload-loop circuit breaker.
//
// The boundary reload is normally self-terminating: after the reload, AuthGate's
// Layer B check finds LS.lastUserId === user.id (written before this reload) and
// takes the no-dispatch path, so no further reload fires. BUT if the
// localStorage.setItem of the marker THROWS (storage pressure / eviction — the
// exact FT-302 Android incident vector), the marker never lands, the next mount
// sees a mismatch again, and we'd reload forever. This fuse caps that.
const RELOAD_FUSE_KEY = 'sr.auth.boundaryReloadCount';
const RELOAD_FUSE_THRESHOLD = 3;

/**
 * FT-365 Option B — clear the boundary-reload fuse counter.
 *
 * Called from AuthGate on a SUCCESSFUL boundary resolution (post-reload, when
 * the LS marker matches the authenticated user and no reload is dispatched), so
 * normal repeated account-switches within one session never falsely trip the
 * fuse. sessionStorage is used so the count is naturally session-scoped and
 * doesn't persist across a real tab close/reopen.
 *
 * @returns {void}
 */
export function clearBoundaryReloadFuse() {
  try {
    // eslint-disable-next-line no-restricted-syntax -- FT-365 fuse counter (sessionStorage, intentional)
    sessionStorage.removeItem(RELOAD_FUSE_KEY);
  } catch (_) { /* non-fatal */ }
}

/**
 * Dispatch the `sr:auth:userChanged` account-boundary event AND reload the
 * page — atomically, in that order.
 *
 * This is the ONE sanctioned dispatcher of `sr:auth:userChanged` in the
 * codebase. Raw `window.dispatchEvent(new CustomEvent('sr:auth:userChanged',
 * ...))` calls are blocked by ESLint (`no-restricted-syntax` in .eslintrc.js).
 *
 * Contract, in order:
 *   1. Writes `sr.auth.lastUserId = userId` to localStorage FIRST. AuthGate's
 *      Layer B boundary check on the next mount compares this LS marker
 *      against the just-authenticated user id, so writing before dispatch is
 *      required for the marker to be present when the reload lands.
 *   2. Dispatches `sr:auth:userChanged` with `{ detail: { userId } }`. In-
 *      flight listeners (bridge belt-and-braces reset, dev-mode invariant
 *      assert) see this before the reload tears down the module registry.
 *   3. Logs `[AUTH-BOUNDARY-RELOAD]` track1 event with `{ userId,
 *      flagEnabled }`. No-op when telemetry gate is off.
 *   4. If `sr.feature.option-b-auth` flag is enabled (default true):
 *      schedules `window.location.reload()` via `setTimeout(_, 0)`. The 0ms
 *      tick lets the dispatch flush + gives listeners a chance to short-
 *      circuit before teardown.
 *   5. If the flag is disabled (rollback path): skips the reload — Layer D
 *      wrapper (VesselProviderKeyedByAuth, gated by the same flag in
 *      TailwindApp.js) handles the remount instead. The LS marker + event
 *      dispatch still happen so downstream code continues to work.
 *
 * Null / undefined / empty userId → early return, no side effects.
 *
 * Every step is wrapped in try/catch — LS quota, disabled cookies, or
 * hostile ad-blockers should never break the auth flow.
 *
 * @param {string} userId — the id of the user who just authenticated.
 *   Missing / falsy → no-op.
 * @returns {void}
 * @see feedback_auth_boundary_reload_invariant.md
 */
export function dispatchUserBoundaryReload(userId) {
  if (!userId) return;

  try {
    // eslint-disable-next-line no-restricted-syntax -- FT-365 boundary marker (LS write is intentional; helper is the sole writer)
    localStorage.setItem(LAST_USER_ID_KEY, userId);
  } catch (_) { /* non-fatal, LS quota */ }

  try {
    // eslint-disable-next-line no-restricted-syntax -- FT-365: helper is the ONLY sanctioned dispatcher of sr:auth:userChanged (see .eslintrc rule)
    window.dispatchEvent(new CustomEvent('sr:auth:userChanged', { detail: { userId } }));
  } catch (_) { /* non-fatal */ }

  const flagEnabled = isOptionBAuthBoundaryEnabled();

  // FT-365 Option B Phase 2: telemetry event immediately BEFORE reload so
  // silent regressions of the invariant (helper called but reload never
  // fires, e.g. flag flipped mid-session) are visible in ring buffer.
  try {
    track1.log('[AUTH-BOUNDARY-RELOAD]', { userId, flagEnabled });
  } catch (_) { /* non-fatal — telemetry never blocks auth */ }

  if (flagEnabled) {
    // FT-365 Option B HARDENING: circuit breaker. Increment a session-scoped
    // counter BEFORE reloading. If we've already reloaded RELOAD_FUSE_THRESHOLD
    // times this session, the reload is not terminating (almost certainly the
    // LS marker setItem keeps throwing under storage pressure) — STOP reloading
    // so we don't spin forever. Falls through without looping; the user stays on
    // whatever the current mount rendered rather than getting a white-screen
    // reload storm.
    let reloadCount = 0;
    try {
      // eslint-disable-next-line no-restricted-syntax -- FT-365 fuse counter (sessionStorage, intentional)
      reloadCount = parseInt(sessionStorage.getItem(RELOAD_FUSE_KEY) || '0', 10) || 0;
      // eslint-disable-next-line no-restricted-syntax -- FT-365 fuse counter (sessionStorage, intentional)
      sessionStorage.setItem(RELOAD_FUSE_KEY, String(reloadCount + 1));
    } catch (_) {
      // sessionStorage itself unavailable — can't fuse. Reload once anyway
      // (single reload is safe; only a LOOP is dangerous, and without a
      // counter we can't detect a loop, so don't make things worse by blocking).
      reloadCount = 0;
    }

    if (reloadCount >= RELOAD_FUSE_THRESHOLD) {
      // Fuse blown — do NOT reload. Log loudly so the loop vector surfaces.
      try {
        track1.log('[AUTH-BOUNDARY-RELOAD-FUSE]', { userId, reloadCount });
      } catch (_) { /* non-fatal */ }
      // eslint-disable-next-line no-console
      console.error(
        '[AUTH-BOUNDARY-RELOAD-FUSE] Boundary reload fired %d times this session ' +
        'without resolving (LS marker likely failing to persist — storage pressure?). ' +
        'Suppressing further reloads to avoid an infinite loop. userId=%s',
        reloadCount, userId
      );
      return;
    }

    // eslint-disable-next-line no-console
    console.info('[AUTH-BOUNDARY-RELOAD] userId=%s reloading page (attempt %d)', userId, reloadCount + 1);
    // Small tick to let the dispatch flush + Layer D short-circuit any listeners
    setTimeout(() => window.location.reload(), 0);
  }
  // If flag OFF: skip reload — Layer D wrapper handles remount (rollback path).
}

/**
 * FT-365 Option B Phase 2 — dev-mode invariant assert.
 *
 * Watches for any dispatch of `sr:auth:userChanged` that isn't followed by a
 * `window.location.reload()` within 100ms. Fires a loud console.error so raw
 * dispatches slipping past the ESLint rule (dynamic strings, third-party libs,
 * DevTools console pokes) are caught during local development.
 *
 * Silent no-op in production builds. Installed once at module import — safe to
 * re-import.
 */
let __ft365DevAssertInstalled = false;
function installDevInvariantAssert() {
  if (__ft365DevAssertInstalled) return;
  if (typeof window === 'undefined') return;
  if (typeof process !== 'undefined' && process.env) {
    const nodeEnv = process.env.NODE_ENV;
    // Skip in production AND test — in unit tests jsdom's window persists
    // across jest.resetModules() calls, so each test import would install
    // another accumulating listener + spam console.error on every dispatch.
    if (nodeEnv === 'production' || nodeEnv === 'test') return;
  }
  __ft365DevAssertInstalled = true;

  let unloadFired = false;
  try {
    window.addEventListener('beforeunload', () => { unloadFired = true; });
    window.addEventListener('sr:auth:userChanged', (event) => {
      setTimeout(() => {
        if (!unloadFired) {
          // eslint-disable-next-line no-console
          console.error(
            '[FT-365 INVARIANT VIOLATION] sr:auth:userChanged dispatched at %o without following window.location.reload() within 100ms. ' +
            'Use dispatchUserBoundaryReload() from src/lib/authBoundary.js. See feedback_auth_boundary_reload_invariant.md',
            event && event.detail
          );
        }
        unloadFired = false; // reset for next event
      }, 100);
    });
  } catch (_) { /* non-fatal */ }
}

installDevInvariantAssert();
